import { DASHBOARD } from 'queries/query-keys';
import { useCallback, useMemo, useState } from 'react';
import DashboardService from 'services/DashboardService';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useTranslation } from 'react-i18next';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import useModal from 'components/Modals/useModal';
import { useReorderDashboards } from 'components/DashboardGrid/hooks/useReorderDashboards';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';
import { getOriginalError } from 'services/AxiosService';
import { getErrorMessage } from 'hooks/useErrors';
export const fetchDashboardsQuery = async (
  searchTerm,
  includeSharing,
  objectId,
  type
) => {
  const res = await DashboardService.getDashboardList(
    searchTerm,
    includeSharing,
    objectId,
    type
  );
  return res;
};

export const fetchOtherDashboardsQuery = async (
  searchTerm,
  page,
  pageSize,
  objectId,
  type
) => {
  const res = await DashboardService.getOthersDashboardList(
    searchTerm,
    page,
    pageSize,
    objectId,
    type
  );
  return res;
};

export const fetchAllDashboardsQuery = async (
  searchTerm = '',
  page = 1,
  includeSharing = false,
  objectId,
  type = 'generic_dashboard'
) => {
  const [dashboards, otherDashboards] = await Promise.all([
    fetchDashboardsQuery(searchTerm, includeSharing, objectId, type),
    fetchOtherDashboardsQuery(
      searchTerm,
      page,
      FORCE_ALL_RECORDS_SIZE,
      objectId,
      type
    ),
  ]);
  return dashboards.concat(otherDashboards?.results || []);
};

export const updateDashboardQuery = (id, values) => {
  return DashboardService.updateDashboard(id, values);
};

export const createDashboardQuery = (values, objectId, type) => {
  return DashboardService.createDashboard(values, objectId, type);
};

export const duplicateDashboardQuery = (values, objectId, type) => {
  return DashboardService.duplicateDashboard(values, objectId, type);
};

export const deleteDashboardQuery = (id) => {
  return DashboardService.deleteDashboard(id);
};

export const reorderDashboardsQuery = (id, payload) => {
  return DashboardService.reorderDashboards(id, payload);
};

export const requestAccessQuery = (dashboardId, adminId, accessLevel) => {
  return DashboardService.requestAccess(dashboardId, adminId, accessLevel);
};

const INCLUDE_SHARING = true;

const useDashboardList = (
  { debouncedTerm = '', objectId = '', type = '' },
  useConfig
) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const [showToast] = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [existingDashboard, setExistingDashboard] = useState({});
  const [isDuplication, setIsDuplication] = useState(false);
  const [canUserEditPermissions, setCanUserEditPermissions] = useState(true);
  const [isDashboardUpdated, setIsDashboardUdated] = useState(false);
  const [backToDashboardId, setBackToDashboardId] = useState(null);
  const [errors, setErrors] = useState({});

  const dashboardsQueryKey = debouncedTerm
    ? [...DASHBOARD.DASHBOARDS, debouncedTerm, objectId, INCLUDE_SHARING]
    : [...DASHBOARD.DASHBOARDS, objectId, INCLUDE_SHARING];

  const otherDashboardsQueryKey = debouncedTerm
    ? [
        ...DASHBOARD.DASHBOARDS_OTHERS,
        currentPage,
        currentPageSize,
        debouncedTerm,
        objectId,
      ]
    : [...DASHBOARD.DASHBOARDS_OTHERS, currentPage, currentPageSize, objectId];

  const handleFetchDashboards = useCallback(() => {
    return fetchDashboardsQuery(debouncedTerm, INCLUDE_SHARING, objectId, type);
  }, [debouncedTerm, objectId, type]);

  const handleFetchOtherDashboards = useCallback(() => {
    return fetchOtherDashboardsQuery(
      debouncedTerm,
      currentPage,
      currentPageSize,
      objectId,
      type
    );
  }, [debouncedTerm, currentPage, currentPageSize, objectId, type]);

  const {
    isLoading: myDashboardsLoading,
    data: myDashboards = [],
    isRefetching: dashboardsRefetching,
  } = useQuery({
    queryKey: dashboardsQueryKey,
    queryFn: handleFetchDashboards,
    placeholderData: keepPreviousData,
  });

  const refetchDashboards = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: DASHBOARD.DASHBOARDS });
  }, [queryClient]);

  const {
    isLoading: otherDashboardsLoading,
    data: otherDashboards = {
      results: [],
      total: 0,
    },
    refetch: refetchOtherDashboards,
    isRefetching: otherDashboardsRefetching,
  } = useQuery({
    queryKey: otherDashboardsQueryKey,
    queryFn: handleFetchOtherDashboards,
    placeholderData: keepPreviousData,
  });

  const data = useMemo(() => {
    return {
      mine: myDashboards,
      other: otherDashboards.results,
    };
  }, [myDashboards, otherDashboards]);

  const loading = useMemo(() => {
    return {
      mine: myDashboardsLoading,
      other: otherDashboardsLoading,
    };
  }, [myDashboardsLoading, otherDashboardsLoading]);

  const refetching = useMemo(() => {
    return {
      mine: dashboardsRefetching,
      other: otherDashboardsRefetching,
    };
  }, [dashboardsRefetching, otherDashboardsRefetching]);

  const {
    loading: configLoading,
    afterDelete,
    afterCreate,
    afterUpdate,
  } = useConfig(data.mine, true, objectId === '' ? type : objectId);

  const updateDashboardMutation = useMutation({
    mutationFn: (result) => updateDashboardQuery(result.id, result.payload),
    onMutate: () => {
      setErrors({});
    },
    onSuccess: (data, variables) => {
      setIsDashboardUdated(true);
      afterUpdate(data.id, variables.payload);
      queryClient.invalidateQueries({
        queryKey: [...DASHBOARD.DASHBOARD, data.id],
      });
      setExistingDashboard({});
      refetchDashboards();
      refetchOtherDashboards();
      handleHideModal();
    },
    onError: (payload) => {
      const originalError = getOriginalError(payload);
      if (originalError?.api_name) {
        setErrors(originalError);
      } else {
        showToast({
          message:
            getErrorMessage(originalError) ??
            t('Dashboard could not be updated.'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const createDashboardMutation = useMutation({
    mutationFn: (result) => createDashboardQuery(result, objectId, type),
    onMutate: () => {
      setErrors({});
    },
    onSuccess: (data) => {
      afterCreate(data.id);
      setBackToDashboardId(data.id);
      refetchDashboards();
      refetchOtherDashboards();
      setExistingDashboard({});
      handleHideModal();
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors);
    },
  });

  const duplicateDashboardMutation = useMutation({
    mutationFn: (result) =>
      duplicateDashboardQuery(result.id, result.isNameChanged, result.payload),
    onSuccess: (data) => {
      afterCreate(data.id);
      setBackToDashboardId(data.id);
      refetchDashboards();
      refetchOtherDashboards();
      setExistingDashboard({});
      setIsDuplication(false);
      handleHideModal();
    },
  });

  const deleteDashboardMutation = useMutation({
    mutationFn: (result) => deleteDashboardQuery(result),
    onSuccess: (_data, id) => {
      setIsDashboardUdated(true);
      afterDelete(id);
      setBackToDashboardId(null);
      refetchDashboards();
      refetchOtherDashboards();
    },
  });

  const sendRequestMutation = useMutation({
    mutationFn: (result) => {
      return requestAccessQuery(
        result.dashboardId,
        result.adminId,
        result.accessLevel
      );
    },
    onSettled: (data, e) => {
      if (data?.status === 'pending') {
        showToast({
          message: t('Your access request has been sent.'),
          variant: toastVariant.SUCCESS,
        });
      } else {
        showToast({
          message: t('Your access request could not be sent.'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const handleMutateDashboard = useCallback(
    (
      { name, api_name, permissions, styleSettings, isPrivate, owner = {} },
      isEditing,
      isNameChanged,
      id,
      isDuplication
    ) => {
      const sharingSettingsPayload = permissions
        ? {
            sharing_settings: {
              ...buildSharingSettingsForAPI(permissions).sharing_settings,
              private: isPrivate,
            },
          }
        : {};
      if (isDuplication) {
        duplicateDashboardMutation.mutate({
          id,
          isNameChanged,
          payload: {
            name,
            style_settings: styleSettings,
            ...sharingSettingsPayload,
            ...owner,
          },
        });
      } else if (isEditing) {
        updateDashboardMutation.mutate({
          id,
          payload: {
            name,
            api_name,
            style_settings: styleSettings,
            ...sharingSettingsPayload,
            ...owner,
          },
        });
      } else {
        createDashboardMutation.mutate({
          name,
          style_settings: styleSettings,
          ...sharingSettingsPayload,
        });
      }
    },
    [
      updateDashboardMutation,
      createDashboardMutation,
      duplicateDashboardMutation,
    ]
  );

  const handleReorderDashboards = useReorderDashboards({
    onSuccess: () => {
      refetchDashboards();
      refetchOtherDashboards();
    },
    queryKey: dashboardsQueryKey,
  });

  const [modalProps, , { show: handleShowModal, hide: handleHideModal }] =
    useModal({
      closeOnSubmit: false,
      handleSubmit: handleMutateDashboard,
      handleHide: () => {
        setExistingDashboard({});
        setIsDuplication(false);
        setCanUserEditPermissions(true);
        setErrors({});
      },
    });

  const [requestModalProps, , { show: handleShowRequestModal }] = useModal({
    handleSubmit: (dashboardId, accessLevel, adminId) =>
      sendRequestMutation.mutate({ dashboardId, accessLevel, adminId }),
    handleHide: () => {
      setExistingDashboard({});
      setIsDuplication(false);
      setCanUserEditPermissions(true);
    },
  });

  const handleStartEditing = useCallback(
    (dashboard, canChangePermissions = true) => {
      setExistingDashboard(dashboard);
      setCanUserEditPermissions(canChangePermissions);
      handleShowModal();
    },
    [handleShowModal]
  );

  const handleStartDuplication = useCallback(
    (dashboard, canChangePermissions = true) => {
      setExistingDashboard(dashboard);
      setIsDuplication(true);
      setCanUserEditPermissions(canChangePermissions);
      handleShowModal();
    },
    [handleShowModal]
  );

  const handleStartRequesting = useCallback(
    (dashboard) => {
      setExistingDashboard(dashboard);
      handleShowRequestModal();
    },
    [handleShowRequestModal]
  );

  const handleSettingsBackClick = useCallback(() => {
    if (isDashboardUpdated) {
      queryClient.invalidateQueries({ queryKey: DASHBOARD.DASHBOARD });
    }
  }, [isDashboardUpdated, queryClient]);

  return {
    modalProps: { ...modalProps, isDuplication: isDuplication },
    requestModalProps,
    canUserEditPermissions,
    existingDashboard,
    handleShowModal,
    handleStartEditing,
    handleStartDuplication,
    handleStartRequesting,
    handleMutateDashboard,
    handleSettingsBackClick,
    data,
    updateDashboardMutation,
    deleteDashboardMutation,
    loading,
    configLoading,
    handleReorderDashboards,
    currentPage,
    currentPageSize,
    backToDashboardId,
    setCurrentPage,
    setCurrentPageSize,
    paginationTotal: otherDashboards.count,
    refetchDashboards,
    refetching,
    errors,
  };
};

export default useDashboardList;
